import {
  ContentItemRequest,
  ContentRequest,
  EpiBlock,
  EpiPage,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  Homepage,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  OrderConfirmationResponse,
  OrderDetailsResponse,
  OrderSearchRequest,
  OrderSearchResponse,
  PageRequest,
  PlaceOrderRequest,
  QuickSearchRequest,
  QuickSearchResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ShippingAddressResponse,
  SiteDefinition,
  SiteSettingsResponse,
  TranslationRequest,
  TranslationResponse,
  UserProfileResponse,
  VerifyTokenRequest,
  VerifyTokenResponse,
  WishlistRequest,
  WishlistResponse,
} from '@api/types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getCurrentLanguage } from '@utils/language.utils';

// Default URL can be overriden
// This is useful to point to different API location
// Place a env file named .env.local in your main directory with content:
// REACT_APP_API_URL=<your_url>
const baseUrl = process.env.REACT_APP_API_URL ?? '/';
const contentApiUrl = process.env.REACT_APP_CONTENT_API_URL ?? 'api/episerver/v3.0';

// RTK Query config
// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: 'api',
  tagTypes: [
    'Cart',
    'Dealer',
    'Wishlist',
    'TechnicalMessage',
    'ProductOrderRequest',
    'BuyersCertificate',
    'NewWarrantyClaimsCount',
  ],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl,
    // TODO - state support may be needed later e.g. for auth header
    prepareHeaders: (headers, { getState }) => {
      headers.set('Accept-Language', getCurrentLanguage());
      headers.set('Access-Control-Allow-Origin', '*');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Content Delivery API endpoints
    getSiteDefinition: builder.query<SiteDefinition[], any>({
      query: () => ({
        url: `${contentApiUrl}/site/`,
      }),
    }),
    getHomepage: builder.query<Homepage[], string>({
      query: (contentUrl) => ({
        url: `${contentApiUrl}/content/`,
        params: {
          contentUrl,
          expand: '*',
          currentPageUrl: window.location.pathname,
        },
      }),
    }),
    getPageByUrl: builder.query<EpiPage[], PageRequest>({
      query: (params) => ({
        url: `${contentApiUrl}/content/`,
        params: { ...params, currentPageUrl: window.location.pathname, matchExact: false },
      }),
    }),
    getContentList: builder.query<EpiBlock[], ContentRequest>({
      query: (params) => ({
        url: `${contentApiUrl}/content/`,
        params: { ...params, currentPageUrl: window.location.pathname },
      }),
    }),
    getContentItemByGuid: builder.query<EpiBlock, ContentItemRequest>({
      query: (params) => ({
        url: `${contentApiUrl}/content/${params.guid}`,
        params: { currentPageUrl: window.location.pathname },
      }),
    }),
    getContentItemAncestors: builder.query<EpiPage[], ContentItemRequest>({
      query: (params) => ({
        url: `${contentApiUrl}/content/${params.guid}/ancestors`,
      }),
    }),

    // SETTINGS
    getSettings: builder.query<SiteSettingsResponse, any>({
      query: () => ({
        url: `api/site/settings`,
      }),
    }),

    // Search
    quickSearch: builder.query<QuickSearchResponse, QuickSearchRequest>({
      query: (params) => ({
        url: `api/content/quick-search`,
        method: 'POST',
        body: params,
      }),
    }),

    // AUTH
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (params) => ({
        url: `api/authentication/login`,
        method: 'POST',
        body: params,
      }),
    }),
    logout: builder.mutation<LogoutResponse, void>({
      query: () => ({
        url: `api/authentication/logout`,
      }),
    }),
    // Reset Password Flow
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (params) => ({
        url: `api/authentication/forgot-password`,
        method: 'POST',
        body: params,
      }),
    }),
    // Reset Password Flow
    verifyToken: builder.query<VerifyTokenResponse, VerifyTokenRequest>({
      query: (params) => ({
        url: `api/authentication/verify-token`,
        method: 'POST',
        body: params,
      }),
    }),
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (params) => ({
        url: `api/authentication/reset-password`,
        method: 'POST',
        body: params,
      }),
    }),
    // User data
    getUserProfile: builder.query<UserProfileResponse, any>({
      query: () => ({
        url: `api/user/profile`,
      }),
    }),

    // ORDERS
    getOrderList: builder.query<OrderSearchResponse, OrderSearchRequest>({
      query: (params) => ({
        method: 'POST',
        url: `api/order/search`,
        body: params,
      }),
    }),

    getOrderDetails: builder.query<OrderDetailsResponse, string>({
      query: (params) => ({
        method: 'GET',
        url: `api/order/details/?vismaOrderNumber=${params}`,
      }),
    }),

    getShippingAddress: builder.query<ShippingAddressResponse, void>({
      query: () => ({
        url: 'api/checkout/shipping-addresses',
      }),
    }),

    getShippingMethods: builder.query<any, void>({
      query: () => ({
        url: 'api/checkout/shipping-methods',
      }),
    }),

    getPaymentMethods: builder.query<any, any>({
      query: () => ({
        url: 'api/checkout/payment-methods',
      }),
    }),

    placeOrder: builder.query<any, PlaceOrderRequest>({
      query: (body) => ({
        method: 'POST',
        url: `api/checkout/place-order`,
        body,
      }),
    }),

    orderConfirmation: builder.query<OrderConfirmationResponse, string>({
      query: (params) => ({
        method: 'GET',
        url: `api/order/confirmation/?vismaOrderNumber=${params}`,
      }),
    }),

    // Translations
    getTranslations: builder.query<TranslationResponse, TranslationRequest>({
      query: (params) => ({
        method: 'GET',
        url: `api/translation/list`,
        params,
      }),
    }),

    getWishlist: builder.query<WishlistResponse, void>({
      query: () => ({
        method: 'GET',
        url: `api/wishlist/current`,
      }),
      providesTags: [{ type: 'Wishlist', id: 'List' }],
    }),

    addToWishlist: builder.mutation<any, WishlistRequest>({
      query: (params) => ({
        method: 'POST',
        url: `api/wishlist/add-item`,
        body: params,
      }),
      invalidatesTags: [{ type: 'Wishlist', id: 'List' }],
    }),

    removeFromWishlist: builder.mutation<any, Omit<WishlistRequest, 'quantity'>>({
      query: (params) => ({
        method: 'POST',
        url: `api/wishlist/remove-item`,
        body: params,
      }),
      invalidatesTags: [{ type: 'Wishlist', id: 'List' }],
    }),

    changeWishlistItemQuantity: builder.mutation<any, WishlistRequest>({
      query: (params) => ({
        method: 'POST',
        url: `api/wishlist/change-item-quantity`,
        body: params,
      }),
      invalidatesTags: [{ type: 'Wishlist', id: 'List' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPageByUrlQuery,
  useLazyGetPageByUrlQuery,
  useGetContentListQuery,
  useLazyGetContentListQuery,
  useGetSiteDefinitionQuery,
  useGetHomepageQuery,
  useGetContentItemByGuidQuery,
  useGetContentItemAncestorsQuery,
  useLoginMutation,
  useLogoutMutation,
  useGetUserProfileQuery,
  useForgotPasswordMutation,
  useVerifyTokenQuery,
  useResetPasswordMutation,
  useLazyQuickSearchQuery,
  useGetSettingsQuery,
  useGetOrderListQuery,
  useLazyGetOrderListQuery,
  useGetOrderDetailsQuery,
  useGetTranslationsQuery,

  useLazyGetShippingAddressQuery,
  useGetShippingMethodsQuery,
  useGetPaymentMethodsQuery,
  usePlaceOrderQuery,
  useLazyPlaceOrderQuery,
  useOrderConfirmationQuery,
  useGetWishlistQuery,
  useLazyGetWishlistQuery,
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
  useChangeWishlistItemQuantityMutation,
} = api;
